
import { Outlet, useLocation } from 'react-router-dom';
import { AuthProvider } from './hooks/useAuth';
import { UserProvider } from './hooks/useUser';
import { SeasonProvider } from './hooks/useSeason';
import { DatabaseStatus } from './components/dev/DatabaseStatus';
import { AppErrorBoundary } from './components/ui/AppErrorBoundary';
import { GlobalLoadingProvider } from './hooks/useLoadingState';
import { useRealtimeUpdates } from './hooks/useRealtimeUpdates';
import { useOnboarding } from './hooks/useOnboarding';
import { useAuth } from './hooks/useAuth';
import { AppLayout } from './components/layout/AppLayout';
import { OnboardingModal, OnboardingLoader } from './components/onboarding';

function AppContent() {
  const { user } = useAuth();
  const location = useLocation();
  
  // Initialize real-time updates
  useRealtimeUpdates();
  
  // Handle onboarding
  const { needsOnboarding, isCheckingOnboarding, completeOnboarding } = useOnboarding();

  // Routes that should not use the sidebar layout (public routes)
  const publicRoutes = ['/', '/login', '/privacy', '/terms', '/contact', '/unsubscribe', '/auth-redirect'];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  // Show loader while checking if onboarding is needed
  if (isCheckingOnboarding) {
    return <OnboardingLoader />;
  }

  // For authenticated users on protected routes, use AppLayout with sidebar
  if (user && !isPublicRoute) {
    return (
      <AppLayout>
        <Outlet />
        <DatabaseStatus />
        <OnboardingModal 
          isOpen={needsOnboarding} 
          onComplete={completeOnboarding} 
        />
      </AppLayout>
    );
  }

  // For public routes or unauthenticated users, render without sidebar layout
  return (
    <div className="min-h-screen">
      <Outlet />
      <DatabaseStatus />
      <OnboardingModal 
        isOpen={needsOnboarding} 
        onComplete={completeOnboarding} 
      />
    </div>
  );
}

export function App() {
  return (
    <AppErrorBoundary>
      <GlobalLoadingProvider>
        <AuthProvider>
          <UserProvider>
            <SeasonProvider>
              <AppContent />
            </SeasonProvider>
          </UserProvider>
        </AuthProvider>
      </GlobalLoadingProvider>
    </AppErrorBoundary>
  );
}

export default App;
